'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does rhode Have Microplastics? Peptide Lip Treatment and the Full Range"
      author="MicroPlastics Research Desk"
      date="July 31, 2026"
      readTime="11 min read"
      heroImage="/blog/heroes/cosmetics.webp"
      heroImageAlt="Does rhode have microplastics Hailey Bieber Peptide Lip Treatment ingredients checked 2026"
    >
      <BlogLastUpdated iso="2026-07-31" formatted="July 31, 2026" />

      <BlogTLDR>
        <strong>Mostly no, with two real exceptions.</strong> We read
        every ingredient list rhode publishes on rhodeskin.com (35
        distinct formulas, checked 30 July 2026). The famous{' '}
        <strong>Peptide Lip Treatment contains no solid synthetic
        polymer particles</strong> &mdash; it is not a microplastic
        product. But it <em>is</em> built on liquid synthetic polymers:{' '}
        <strong>hydrogenated polyisobutene is the first ingredient and
        polybutene the fourth</strong>, both petroleum-derived. The two
        genuine finds: <strong>Peptide Lip Shape</strong> lip liners list{' '}
        <strong>polyethylene</strong>, and <strong>Barrier Restore
        Cream, Caffeine Reset and Pineapple Refresh Cleanser</strong>{' '}
        list <strong>acrylates copolymer</strong>. The cleanser matters
        most, rinse-off is the first category the EU restricts, in{' '}
        <strong>October 2027</strong>. Overall rhode came out{' '}
        <strong>cleaner than we expected</strong>: 7 of 35 formulas.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>Peptide Lip Treatment: no solid polymer particles.</strong> If your question is &ldquo;is the lip treatment a microplastic product,&rdquo; the answer is no.</>,
          <>But its base is <strong>liquid synthetic polymer</strong>, hydrogenated polyisobutene and polybutene. Petroleum-derived, not plant-derived. That is a fair thing to object to; it is just not a microplastic.</>,
          <><strong>Peptide Lip Shape</strong> (the lip liner) lists <strong>polyethylene</strong> across all shades. Standard wax hardener, and a genuine solid polymer.</>,
          <>Three rhode skincare products list <strong>acrylates copolymer</strong>: Barrier Restore Cream, Caffeine Reset, Pineapple Refresh Cleanser.</>,
          <>rhode publishes full ingredient lists, per shade, on its own product pages. That is better disclosure than most of the category and it is why this article can be specific.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The question people are actually asking
      </h2>
      <p>
        rhode launched in 2022, was acquired by e.l.f. Beauty in 2025,
        and its Peptide Lip Treatment became one of the most
        photographed beauty products of the decade. &ldquo;Glazed&rdquo;
        became a texture people ask for by name.
      </p>
      <p>
        Which means the search traffic is enormous and the information
        is bad. The usual claim is that the lip treatment is
        &ldquo;basically Vaseline&rdquo; or &ldquo;liquid plastic.&rdquo;
        One of those is closer than the other, so let us do it properly.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Peptide Lip Treatment, ingredient by ingredient
      </h2>
      <p>
        This is the list rhode published on its own product page on{' '}
        <strong>30 July 2026</strong>, in order:
      </p>
      <p className="text-sm bg-[#F7F5F2] p-4 rounded-lg border border-black/5">
        Hydrogenated Polyisobutene, Diisostearyl Malate, Butyrospermum
        Parkii (Shea) Butter, Polybutene, Microcrystalline Wax,
        Synthetic Beeswax, Synthetic Wax, Octyldodecanol, Hydrogenated
        Poly(C6-14 Olefin), Polyglyceryl-2 Triisostearate, Dimer
        Dilinoleyl Dimer Dilinoleate, Glyceryl Behenate/Eicosadioate,
        Triethyl Citrate, Tocopherol, Tocopheryl Acetate,
        Tetrahexyldecyl Ascorbate, Palmitoyl Tripeptide-1, Orbignya
        Oleifera Seed Oil*, Theobroma Grandiflorum Seed Butter, Lactic
        Acid, Tribehenin, Stevioside, Ethylhexyl Palmitate, Sorbitan
        Isostearate, Synthetic Fluorphlogopite, Disteardimonium
        Hectorite, o-Cymen-5-ol
      </p>
      <p>
        <strong>Solid synthetic polymer particles: none.</strong> No
        nylon, no PMMA, no polyethylene, no PET, no acrylates. On the
        legal definition of a microplastic, this product is clean, and
        we are not going to pretend otherwise for a better headline.
      </p>
      <p>
        <strong>What is actually in it:</strong> the first ingredient,{' '}
        <em>hydrogenated polyisobutene</em>, is a synthetic
        petroleum-derived polymer used as a non-greasy emollient. So is{' '}
        <em>polybutene</em> at number four, the tacky one, it is what
        makes gloss cling and look wet. So is <em>hydrogenated
        poly(C6-14 olefin)</em> at nine.
      </p>
      <p>
        So the product is, by weight, substantially liquid synthetic
        polymer. Those polymers are liquids, they dissolve nothing, they
        shed no particles, and they sit outside the EU microplastics
        definition, which requires a <strong>solid</strong> particle.
      </p>
      <p>
        Here is the honest framing: <strong>&ldquo;contains no
        microplastics&rdquo; and &ldquo;is mostly petrochemical&rdquo;
        are both true at the same time.</strong> If your objection to
        plastic is about particles in the environment and in bodies, the
        lip treatment is not your problem. If your objection is
        &ldquo;I don&apos;t want petroleum-derived synthetics on my
        mouth,&rdquo; it very much is, and a shea/castor/jojoba balm is
        your swap.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Where rhode does contain solid polymers
      </h2>

      <BlogComparisonTable
        caption="rhode products whose published ingredient list includes a solid synthetic polymer (checked 30 July 2026)"
        columns={[
          { key: 'product', label: 'Product' },
          { key: 'polymer', label: 'Solid polymer listed' },
          { key: 'cat', label: 'EU category / deadline' },
          { key: 'verdict', label: 'How much it matters' },
        ]}
        rows={[
          { product: 'Peptide Lip Shape (lip liner, all shades)', polymer: 'Polyethylene', cat: <>Lip product — Oct 2035</>, verdict: 'Real, but it is the wax hardener that makes a pencil a pencil. Low concentration, sits on the lip.' },
          { product: 'Pineapple Refresh Cleanser', polymer: 'Acrylates Copolymer', cat: <>Rinse-off — Oct 2027</>, verdict: 'The one we would change first. Rinse-off goes straight down the drain, which is exactly why the EU restricts this category first.' },
          { product: 'Barrier Restore Cream', polymer: 'Acrylates Copolymer', cat: <>Leave-on — Oct 2029</>, verdict: 'Film-former for texture. Stays on skin rather than going down a drain.' },
          { product: 'Caffeine Reset (eye)', polymer: 'Acrylates Copolymer', cat: <>Leave-on — Oct 2029</>, verdict: 'Same as above.' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        And where it is clean
      </h2>
      <p>
        28 of the 35 rhode formulas we captured contained{' '}
        <strong>no</strong> solid synthetic polymer particle, including
        the Peptide Lip Treatment in every flavour, the Peptide Lip Tint,
        Glazing Milk and the Glazing Fluid. For a brand whose entire
        aesthetic is built on a plastic-looking finish, that is a better
        result than the marketing would lead a sceptic to guess.
      </p>
      <p>
        Worth saying plainly, because it cuts against the incentive to
        write a takedown: <strong>rhode publishes complete, per-shade
        ingredient lists on its own product pages.</strong> Several
        larger brands in this category do not. We could only write this
        article because rhode made it possible.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The swaps, if you want them
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Instead of the lip liner:</strong> any pencil built on candelilla, carnauba or beeswax rather than polyethylene. Read the list, the wax type is usually in the first five ingredients.</li>
        <li><strong>Instead of the cleanser:</strong> a cream or oil cleanser without acrylates copolymer. This is the single highest-value swap in the whole rhode range, because rinse-off is where the environmental case is strongest.</li>
        <li><strong>If you want the glazed look without petrochemicals:</strong> castor-seed-oil-based balms give the same wet shine. Castor oil is the classic gloss base and it is a plant.</li>
        <li><strong>If you only care about microplastics:</strong> keep the Peptide Lip Treatment. It passes.</li>
      </ul>

      <BlogAppCTA
        headline="Check your lip products in 5 seconds"
        sub="Polyethylene, acrylates copolymer, nylon-12, PMMA. Scan any lip or skincare product and the app separates solid polymer particles from liquid synthetic polymers, tells you which EU deadline it falls under, and shows a cleaner swap."
        buttonLabel="Scan a lip product"
      />

      <p>
        This is one of five brand breakdowns from our{' '}
        <Link href="/blog/microplastics-in-makeup-brands-audited" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          audit of 81 formulas across Fenty, Huda Beauty, rhode and Kylie Cosmetics
        </Link>
        . See also{' '}
        <Link href="/blog/does-fenty-beauty-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          Fenty Beauty
        </Link>
        ,{' '}
        <Link href="/blog/does-huda-beauty-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          Huda Beauty
        </Link>
        ,{' '}
        <Link href="/blog/does-kylie-cosmetics-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          Kylie Cosmetics
        </Link>
        , and{' '}
        <Link href="/blog/clean-beauty-microplastics-loophole" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          why clean-beauty standards don&apos;t catch any of this
        </Link>
        .
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>The full INCI list for the exact rhode SKU and shade from the barcode, not the product line average.</>,
          <>Solid polymer particles (polyethylene, acrylates copolymer) flagged separately from liquid synthetic polymers (hydrogenated polyisobutene, polybutene).</>,
          <>Whether the product is rinse-off or leave-on, which determines both the environmental case and the EU deadline.</>,
          <>Petrochemical-derived emollients, for people whose objection is sourcing rather than particles.</>,
          <>A cleaner same-format swap when the product you scanned contains a solid polymer.</>,
        ]}
      />

      <BlogFAQ
        items={[
          {
            question: 'Does rhode Peptide Lip Treatment contain microplastics?',
            answer:
              'No. The ingredient list rhode published on its own site on 30 July 2026 contains no solid synthetic polymer particles, no nylon, PMMA, polyethylene, PET or acrylates. It does not meet the EU definition of a product containing synthetic polymer microparticles. Its base is liquid synthetic polymer (hydrogenated polyisobutene, polybutene), which is petroleum-derived but is not a microplastic because the definition requires a solid particle.',
          },
          {
            question: 'Is rhode Peptide Lip Treatment just Vaseline?',
            answer:
              'No. Petroleum jelly is a semi-solid mixture of mineral oils and waxes. The rhode lip treatment is built on hydrogenated polyisobutene and polybutene, which are synthetic polymers manufactured from petroleum feedstock rather than refined mineral oil, plus shea butter, waxes and a peptide. Both are petroleum-derived in origin. They are not the same material and they do not behave the same way, polybutene in particular is what creates the tacky, high-shine glaze that petroleum jelly does not.',
          },
          {
            question: 'Which rhode products contain plastic?',
            answer:
              'Of the 35 rhode formulas we captured on 30 July 2026, four contained a solid synthetic polymer. Peptide Lip Shape lip liners list polyethylene. Barrier Restore Cream, Caffeine Reset and Pineapple Refresh Cleanser list acrylates copolymer. The remaining 28, including every Peptide Lip Treatment flavour, the Peptide Lip Tint, Glazing Milk and Glazing Fluid, did not.',
          },
          {
            question: 'Is hydrogenated polyisobutene a microplastic?',
            answer:
              'No. It is a liquid synthetic polymer used as an emollient, and the EU definition of a synthetic polymer microparticle requires a solid particle that is poorly water-soluble. Hydrogenated polyisobutene is a liquid, so it falls outside the restriction. It is still a petroleum-derived synthetic, so it is a reasonable thing to avoid on sourcing grounds, it is simply not a microplastic and calling it one muddies a distinction that matters.',
          },
          {
            question: 'Is rhode cleaner than other celebrity beauty brands?',
            answer:
              'On the specific test of solid synthetic polymer particles, rhode performed better than Fenty Beauty and Huda Beauty in our audit, with 7 of 35 captured formulas versus 8 of 14 and 5 of 12 respectively. We would not push that comparison too hard, the sample sizes are very different because brands publish ingredient data to different degrees. What is fair to say is that rhode publishes more complete per-shade ingredient information than most of its peers, which is itself worth something.',
          },
          {
            question: 'When does the EU ban apply to rhode products?',
            answer:
              'It depends on the product. Pineapple Refresh Cleanser is a rinse-off product, so 17 October 2027. Barrier Restore Cream and Caffeine Reset are leave-on skincare, so 17 October 2029. Peptide Lip Shape is a lip product, so 17 October 2035. None of these products is currently in breach of anything, the restriction phases in over more than a decade.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'rhode skin',
            year: 2026,
            title: 'Published product ingredient lists (Peptide Lip Treatment, Peptide Lip Shape, Barrier Restore Cream, Caffeine Reset, Pineapple Refresh Cleanser), retrieved 30 July 2026',
            publisher: 'rhodeskin.com',
            url: 'https://www.rhodeskin.com/products/peptide-lip-treatment',
          },
          {
            authors: 'European Commission',
            year: 2023,
            title: 'Commission Regulation (EU) 2023/2055 amending Annex XVII to REACH as regards synthetic polymer microparticles',
            publisher: 'Official Journal of the European Union (EUR-Lex, CELEX 32023R2055)',
            url: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A32023R2055',
          },
          {
            authors: 'European Chemicals Agency',
            year: 2023,
            title: 'Microplastics: restriction on intentionally added microplastics',
            publisher: 'ECHA',
            url: 'https://echa.europa.eu/hot-topics/microplastics',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
